import type { Direction, IconComponent } from 'uikit';
import { Tooltip, useDebounce } from 'uikit';
import { formatNumberToRu } from 'utils';
import { replacerCost, replacerCostIfValue } from 'utils/replacerCost';

import type { ICellRendererParams } from 'ag-grid-community';
import type React from 'react';
import type { Ref } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { TextFieldPropSize, TextFieldPropView, TextFieldPropWidth } from '../FieldNumberFormat';
import { FieldNumberFormat } from '../FieldNumberFormat';

export interface IAgNumberValueCellEditor extends ICellRendererParams {
    decimalScale?: number;
    isEditable?: boolean;
    idKey: string;
    columnKey: string;
    debounceTime?: number;
    onChange: (value: number | null) => void;
    disabled?: boolean;
    width?: TextFieldPropWidth;
    view?: TextFieldPropView;
    size?: TextFieldPropSize;
    className?: string;
    leftSide?: string | IconComponent;
    status?: 'alert' | 'success' | 'warning';
    tooltipStatus?: 'alert' | 'success' | 'warning';
    tooltipMessage?: string;
    tooltipDirection?: Direction;
    isCommitting?: boolean;
    isNullPossible?: boolean;
    showTooltipOnHover?: boolean;
    defaultIfEmpty?: string;
    tooltipClassName?: string;
    suppressGridUpdate?: boolean;
}

const onKeyUp = (event: any) => event.stopPropagation();

export const AgNumberValueCellEditingRenderer: React.FC<IAgNumberValueCellEditor> = (props) => {
    const {
        value: defaultValue,
        valueFormatted,
        decimalScale = 0,
        isEditable,
        api,
        data,
        idKey,
        columnKey,
        debounceTime,
        onChange,
        disabled,
        width,
        view,
        size = 's',
        className,
        leftSide,
        status,
        tooltipStatus,
        tooltipMessage,
        tooltipDirection,
        isCommitting,
        isNullPossible,
        showTooltipOnHover,
        defaultIfEmpty = '',
        tooltipClassName,
        suppressGridUpdate,
    } = props;

    // TODO Это, конечно, ещё одна засада, с ещё одним не совсем понятно зачем нужным внутренним состоянием
    const [currentValue, setCurrentValue] = useState<string | null>(null);
    const [isTooltipVisible, setIsTooltipVisible] = useState(!showTooltipOnHover);
    const inputRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        const inputRoot = inputRef.current;

        if (inputRoot?.contains(document.activeElement)) {
            return;
        }

        setCurrentValue(formatNumberToRu(defaultValue, decimalScale) ?? (disabled ? '' : defaultIfEmpty));
    }, [decimalScale, defaultIfEmpty, defaultValue, disabled]);

    const setFieldValue = useCallback(
        (value?: string | null) => {
            if (suppressGridUpdate) {
                return;
            }

            const rowNode = api.getRowNode(String(data[idKey]));

            if (rowNode) {
                rowNode.setDataValue(columnKey, typeof value === 'undefined' ? currentValue : value);
            }
        },
        [api, columnKey, currentValue, data, idKey, suppressGridUpdate],
    );

    const onKeyDown = useCallback(
        (event) => {
            event.stopPropagation();

            if (event.key === 'Enter') {
                setFieldValue();
            }
        },
        [setFieldValue],
    );

    const onBlur = useCallback(() => {
        setFieldValue();
    }, [setFieldValue]);

    const debouncedChange = useDebounce(onChange, debounceTime as number);

    const onChangeLocal = useCallback(
        ({ value }: { value: string | null }) => {
            if (value === '-') {
                return;
            }

            setCurrentValue(value);
            setFieldValue(value);

            const val = isNullPossible ? replacerCostIfValue(value) : replacerCost(value);

            if (debounceTime) {
                if (!suppressGridUpdate) {
                    const rowNode = api.getRowNode(String(data[idKey]));

                    if (rowNode) {
                        rowNode.setDataValue(columnKey, value);
                    }
                }

                debouncedChange(val);
            } else {
                onChange(val);
            }
        },
        [
            api,
            columnKey,
            data,
            debounceTime,
            debouncedChange,
            idKey,
            isNullPossible,
            onChange,
            setFieldValue,
            suppressGridUpdate,
        ],
    );

    const onMouseOver = useCallback(() => {
        setIsTooltipVisible(true);
    }, []);

    const onMouseOut = useCallback(() => {
        setIsTooltipVisible(false);
    }, []);

    return isEditable ? (
        <>
            <FieldNumberFormat
                value={currentValue}
                decimalScale={decimalScale}
                size={size}
                onChange={onChangeLocal}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
                disabled={disabled}
                width={width}
                view={view}
                className={className}
                leftSide={leftSide}
                status={status}
                inputContainerRef={inputRef as Ref<HTMLDivElement>}
                isCommitting={isCommitting}
                onMouseOver={showTooltipOnHover ? onMouseOver : undefined}
                onMouseOut={showTooltipOnHover ? onMouseOut : undefined}
                inputStyle={{ minWidth: 'unset' }}
            />
            {tooltipMessage && isTooltipVisible && (
                <Tooltip
                    size="s"
                    direction={tooltipDirection}
                    status={tooltipStatus}
                    anchorRef={inputRef}
                    className={tooltipClassName}
                >
                    {tooltipMessage}
                </Tooltip>
            )}
        </>
    ) : (
        <span>{valueFormatted}</span>
    );
};

AgNumberValueCellEditingRenderer.displayName = 'AgNumberValueCellEditingRenderer';
